import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "../../components/Toast/toast.ts";
import moment from "moment";
import { ProgressSpinner } from "primereact/progressspinner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Store,
  FileText,
  MessageSquare,
  Send,
  Hash,
  Tag,
  AlignLeft,
  ClipboardList,
} from "lucide-react";
import DescriptionBox from "../../components/DescriptionBox/index.tsx";
import { StatusChip } from "../../components/StatusChip/index.ts";

/* ─── Section Card ─── */
const SectionCard = ({
  title,
  icon: Icon,
  children,
  headerRight,
  className = "",
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] overflow-hidden ${className}`}
  >
    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[rgb(var(--border-primary))] bg-[rgb(var(--surface-2)/0.5)]">
      <div className="w-6 h-6 rounded-md bg-gold-2/10 flex items-center justify-center">
        <Icon size={12} className="text-gold-2" />
      </div>
      <span className="text-[13px] font-semibold text-[rgb(var(--text-primary))]">
        {title}
      </span>
      {headerRight && <div className="ml-auto">{headerRight}</div>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

/* ─── Info Row ─── */
const InfoRow = ({
  icon: Icon,
  label,
  value,
  children,
}: {
  icon: any;
  label: string;
  value?: string;
  children?: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 py-3.5 border-b border-[rgb(var(--border-primary))] last:border-b-0">
    <div className="w-7 h-7 rounded-md bg-[rgb(var(--surface-3))] flex items-center justify-center shrink-0 mt-0.5">
      <Icon size={13} className="text-gold-2" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-0.5">
        {label}
      </p>
      {children ?? (
        <p className="text-[13px] text-[rgb(var(--text-primary))] font-medium leading-snug">
          {value || "—"}
        </p>
      )}
    </div>
  </div>
);

/* ─── Skeleton ─── */
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div
    className={`rounded-md bg-[rgb(var(--surface-3))] animate-pulse ${className}`}
  />
);

const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-1 flex flex-col gap-4">
      <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-5 flex flex-col gap-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 py-2">
            <Skeleton className="w-7 h-7 shrink-0" />
            <div className="flex-1">
              <Skeleton className="h-2.5 w-16 mb-1.5" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="lg:col-span-2 flex flex-col gap-4">
      <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-5">
        <Skeleton className="h-4 w-24 mb-4" />
        <Skeleton className="h-20 w-full" />
      </div>
      <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-5">
        <Skeleton className="h-4 w-32 mb-4" />
        <div className="flex flex-col gap-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const CustomQueryPage: React.FC = () => {
  const { customRequestId } = useParams();
  const navigate = useNavigate();
  const storedToken = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [reload, setReload] = useState(false);
  const [customQueryDetails, setCustomQueryDetails] = useState<any>(null);
  const [commentsVal, setCommentsVal] = useState("");

  /* Formatter */
  const formatCommentDate = (d: string) =>
    moment(d).format("MMM DD, YYYY · hh:mm A");

  /* Fetch */
  const getCustomQueryDetails = async () => {
    if (!storedToken) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/custom-query-request/${customRequestId}`,
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      setCustomQueryDetails(response?.data);
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCustomQueryDetails();
    // eslint-disable-next-line
  }, [reload]);

  /* Add comment */
  const handleAddComment = async () => {
    if (!commentsVal.trim()) return;
    try {
      setSubmittingComment(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/custom-query-request/comment`,
        { id: customRequestId, payload: commentsVal },
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      if (response?.status === 200) {
        toast.success(response?.data?.message);
      }
      setCommentsVal("");
      setReload((r) => !r);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Something went wrong while adding comment!"
      );
    } finally {
      setSubmittingComment(false);
    }
  };

  /* Derived */
  const { custom_query_subject, custom_query_details, custom_query_status, store_name } =
    customQueryDetails || {};

  const statusVariant =
    custom_query_status === "Resolved" || custom_query_status === "Completed"
      ? "success"
      : custom_query_status === "Pending"
      ? "warning"
      : custom_query_status === "Rejected"
      ? "danger"
      : "neutral";

  /* ── render ── */
  return (
    <div className="flex flex-col gap-6">
      {/* ── Page Header ── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg border border-[rgb(var(--border-primary))] bg-[rgb(var(--surface-1))] hover:bg-[rgb(var(--surface-2))] text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
        >
          <ArrowLeft size={15} />
        </button>
        <DescriptionBox
          title="Client Queries"
          subTitle="Custom Request Detail"
          description="Full detail view for this custom query request."
        />
        {custom_query_status && !loading && (
          <div className="ml-auto">
            <StatusChip label={custom_query_status} variant={statusVariant} />
          </div>
        )}
      </div>

      {/* ── Body ── */}
      {loading ? (
        <LoadingSkeleton />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          {/* ── LEFT COLUMN ── */}
          <div className="lg:col-span-1 flex flex-col gap-4">

            {/* Core Info */}
            <SectionCard title="Request Info" icon={ClipboardList}>
              <InfoRow
                icon={Hash}
                label="Request ID"
                value={customRequestId?.toString().slice(-8).toUpperCase()}
              />
              <InfoRow icon={Store} label="Store" value={store_name} />
              <InfoRow icon={Tag} label="Subject" value={custom_query_subject} />
            </SectionCard>

            {/* Status */}
            {custom_query_status && (
              <SectionCard title="Request Status" icon={FileText}>
                <div
                  className={`flex items-center gap-3 p-4 rounded-lg border ${
                    statusVariant === "success"
                      ? "bg-[rgb(var(--success)/0.08)] border-[rgb(var(--success)/0.25)]"
                      : statusVariant === "warning"
                      ? "bg-[rgb(var(--warning)/0.08)] border-[rgb(var(--warning)/0.25)]"
                      : statusVariant === "danger"
                      ? "bg-[rgb(var(--danger)/0.08)] border-[rgb(var(--danger)/0.25)]"
                      : "bg-[rgb(var(--surface-2))] border-[rgb(var(--border-primary))]"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full shrink-0 animate-pulse ${
                      statusVariant === "success"
                        ? "bg-[rgb(var(--success))]"
                        : statusVariant === "warning"
                        ? "bg-[rgb(var(--warning))]"
                        : statusVariant === "danger"
                        ? "bg-[rgb(var(--danger))]"
                        : "bg-[rgb(var(--text-muted))]"
                    }`}
                  />
                  <div>
                    <p
                      className={`text-[14px] font-bold ${
                        statusVariant === "success"
                          ? "text-[rgb(var(--success))]"
                          : statusVariant === "warning"
                          ? "text-[rgb(var(--warning))]"
                          : statusVariant === "danger"
                          ? "text-[rgb(var(--danger))]"
                          : "text-[rgb(var(--text-secondary))]"
                      }`}
                    >
                      {custom_query_status}
                    </p>
                    <p className="text-[11px] text-[rgb(var(--text-muted))] mt-0.5">
                      Current request status
                    </p>
                  </div>
                </div>
              </SectionCard>
            )}
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="lg:col-span-2 flex flex-col gap-4">

            {/* Query Details */}
            <SectionCard title="Query Details" icon={AlignLeft}>
              {custom_query_details ? (
                <p className="text-[13px] text-[rgb(var(--text-secondary))] leading-relaxed whitespace-pre-wrap">
                  {custom_query_details}
                </p>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <AlignLeft
                    size={24}
                    className="text-[rgb(var(--text-muted))] opacity-30"
                  />
                  <p className="text-[12px] text-[rgb(var(--text-muted))]">
                    No details provided.
                  </p>
                </div>
              )}
            </SectionCard>

            {/* Comments */}
            <SectionCard
              title="Comments History"
              icon={MessageSquare}
              headerRight={
                <span className="text-[11px] text-[rgb(var(--text-muted))] bg-[rgb(var(--surface-2))] px-2 py-0.5 rounded-full">
                  {customQueryDetails?.comments?.length || 0}
                </span>
              }
            >
              <div className="flex flex-col gap-3">
                {/* Comment list */}
                {customQueryDetails?.comments?.length > 0 ? (
                  <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-1">
                    <AnimatePresence>
                      {customQueryDetails.comments.map(
                        (item: any, index: number) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.04, duration: 0.25 }}
                            className="flex gap-3 p-3 rounded-lg bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-primary))]"
                          >
                            <div className="w-7 h-7 rounded-full bg-gold-2/15 flex items-center justify-center shrink-0 text-[11px] font-bold text-gold-2">
                              {item?.comment_by?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[12px] font-semibold text-[rgb(var(--text-primary))]">
                                  {item?.comment_by || "Unknown"}
                                </span>
                                <span className="text-[10px] text-[rgb(var(--text-muted))]">
                                  {formatCommentDate(item?.comment_date)}
                                </span>
                              </div>
                              <p className="text-[12px] text-[rgb(var(--text-secondary))] leading-relaxed">
                                {item?.comment}
                              </p>
                            </div>
                          </motion.div>
                        )
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 gap-2">
                    <MessageSquare
                      size={24}
                      className="text-[rgb(var(--text-muted))] opacity-30"
                    />
                    <p className="text-[12px] text-[rgb(var(--text-muted))]">
                      No comments yet
                    </p>
                  </div>
                )}

                {/* Add comment */}
                <div className="border-t border-[rgb(var(--border-primary))] pt-4 mt-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-2">
                    Add Comment
                  </p>
                  <div className="flex flex-col gap-2">
                    <textarea
                      value={commentsVal}
                      onChange={(e) => setCommentsVal(e.target.value)}
                      placeholder="Type your comment here..."
                      rows={3}
                      className="w-full px-3 py-2.5 text-[13px] rounded-md border border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-2))] text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-muted))] outline-none focus:border-gold-2 focus:ring-1 focus:ring-gold-2/20 transition-all resize-none"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={handleAddComment}
                        disabled={!commentsVal.trim() || submittingComment}
                        style={{background: "var(--gold-grad)"}}
                        className="flex items-center gap-2 px-4 h-[38px] rounded-md hover:bg-gold-3 text-white text-[12px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {submittingComment ? (
                          <ProgressSpinner
                            style={{ width: "16px", height: "16px" }}
                            strokeWidth="3"
                            animationDuration=".5s"
                          />
                        ) : (
                          <Send size={13} />
                        )}
                        Submit Comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>

          </div>
        </motion.div>
      )}
    </div>
  );
};

export default CustomQueryPage;